/**
 * MeetTrouve — "Meet Trouvé" homepage section, above the fold.
 *
 * What it is: a horizontal card sitting at the top of the Welcome hero
 * (for guest users only — signed-in users already know Trouvé). It's
 * 3 things side-by-side:
 *   1. A large brandmark avatar (gold→mauve gradient circle + Playfair T)
 *   2. The 3-sentence character introduction
 *   3. One example coaching tip in a quoted speech-bubble style
 *
 * Goal per the founder: "People immediately understand this is not
 * another generic beauty AI." So the brandmark is large, the copy
 * names the character explicitly ("Hi, I'm Trouvé"), and the example
 * tip is specific enough to be unmistakable — not "we'll help you
 * apply foundation" but "we place blush slightly higher because it
 * visually lifts the cheekbones."
 *
 * Analytics:
 *   • `trouve_intro_viewed` fires once when the section enters viewport.
 *     IntersectionObserver, so it counts actual eyeballs, not scroll-
 *     past-without-stopping cases.
 */
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight } from "lucide-react";
import { track } from "../lib/analytics";
import TrouveShareCard from "./TrouveShareCard";

const MeetTrouve = () => {
  const { t } = useTranslation();
  const ref = useRef(null);

  // Fire `trouve_intro_viewed` when the section is at least 50% in view.
  // IntersectionObserver makes this efficient — no scroll listener.
  // Fired once per page load (the React state inside the closure ensures
  // we don't double-emit on quick scroll-up-scroll-down).
  useEffect(() => {
    if (!ref.current) return;
    let fired = false;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && e.intersectionRatio >= 0.5 && !fired) {
            fired = true;
            track("trouve_intro_viewed");
            observer.disconnect();
          }
        }
      },
      { threshold: [0, 0.5, 1] },
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      data-testid="meet-trouve-section"
      className="relative max-w-6xl mx-auto px-6 sm:px-10 pt-28 sm:pt-32"
    >
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.65, ease: "easeOut" }}
        className="bg-gradient-to-br from-white/90 to-[#FBF4EF]/80 backdrop-blur border border-[#EFE3DC] rounded-3xl p-6 sm:p-8 shadow-[0_30px_80px_-50px_rgba(155,106,108,0.35)]"
      >
        <div className="flex flex-col md:flex-row md:items-stretch gap-7 md:gap-10">
          {/* ─── Trouvé hero illustration ──────────────────────────────
              Editorial fashion illustration (generated 2026-06-06 via
              Nano Banana per the character-design brief). She is the
              embodiment of the brand — drag mother / beauty mentor /
              mirror reflection — NOT a generic gradient avatar.

              Layout: on desktop we showcase the full HERO portrait
              (3:4 vertical) at ~280–340 px, with a champagne-gold
              ring + soft pillow-shadow. On mobile we collapse to the
              compact AVATAR (1:1) so the card stays readable on a
              phone.

              Both <img>s fall back to a gradient brandmark via
              onError so the section never breaks if either asset
              404s in production. */}
          <div className="shrink-0 flex md:block items-center gap-4 md:gap-0">
            {/* Mobile: small avatar (≤ md) */}
            <div
              className="relative h-24 w-24 rounded-full overflow-hidden shadow-[0_14px_40px_-12px_rgba(155,106,108,0.6)] ring-2 ring-[#D4B483]/40 md:hidden"
              aria-hidden="true"
              data-testid="meet-trouve-avatar"
            >
              <img
                src="/trouve/conversation-avatar.png"
                alt=""
                className="h-full w-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  e.currentTarget.nextElementSibling.style.display = "flex";
                }}
              />
              <div
                style={{
                  display: "none",
                  background: "linear-gradient(135deg, #D5A8A4 0%, #9B6A6C 100%)",
                }}
                className="absolute inset-0 items-center justify-center font-serif text-4xl text-white"
              >
                T
              </div>
            </div>

            {/* Desktop: large hero portrait (md+) */}
            <div
              className="hidden md:block relative w-[280px] lg:w-[320px] aspect-[3/4] rounded-[2rem] overflow-hidden shadow-[0_30px_70px_-25px_rgba(93,64,80,0.55)] ring-1 ring-[#D4B483]/30"
              aria-hidden="true"
              data-testid="meet-trouve-portrait"
            >
              <img
                src="/trouve/half-body.png"
                alt=""
                className="h-full w-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  e.currentTarget.nextElementSibling.style.display = "flex";
                }}
              />
              <div
                style={{
                  display: "none",
                  background: "linear-gradient(135deg, #D5A8A4 0%, #9B6A6C 100%)",
                }}
                className="absolute inset-0 items-center justify-center font-serif text-7xl text-white"
              >
                T
              </div>
              {/* Soft inner highlight to lift the portrait from the card */}
              <span
                className="absolute inset-0 rounded-[2rem] pointer-events-none"
                style={{
                  boxShadow: "inset 0 0 0 3px rgba(255,255,255,0.18)",
                }}
              />
            </div>
            <span
              className="hidden md:inline-block mt-3 text-[10px] tracking-[0.22em] uppercase text-[#9B6A6C] font-semibold"
              data-testid="meet-trouve-label"
            >
              {t("meetTrouve.label")}
            </span>
          </div>

          {/* ─── Intro copy ─────────────────────────────────────────── */}
          <div className="flex-1 min-w-0">
            <p className="font-serif text-2xl sm:text-3xl text-[#2C2123] leading-[1.25] mb-4">
              {t("meetTrouve.intro.greeting")}{" "}
              <span className="italic text-[#9B6A6C]">
                {t("meetTrouve.intro.role")}
              </span>
              {t("meetTrouve.intro.suffix")}
            </p>
            <p className="text-[15px] text-[#5C4B51] leading-relaxed mb-5 max-w-xl">
              {t("meetTrouve.intro.body")}
            </p>

            {/* Example coaching tip — quoted speech-bubble style */}
            <div
              className="bg-[#9B6A6C]/[0.07] border-l-2 border-[#9B6A6C] rounded-r-2xl p-4 mb-5"
              data-testid="meet-trouve-example-tip"
            >
              <div className="flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-[#9B6A6C] font-semibold mb-2">
                <Sparkles className="h-3 w-3" />
                {t("meetTrouve.exampleEyebrow")}
              </div>
              <p className="font-serif text-[17px] sm:text-lg text-[#2C2123] leading-snug italic mb-3">
                &ldquo;{t("meetTrouve.exampleQuote")}&rdquo;
              </p>
              {/* Share this tip — turns Trouvé's quote into a branded
                  PNG card, native Web Share API + PNG download fallback */}
              <TrouveShareCard
                tipText={t("meetTrouve.exampleQuote")}
                contextId="meet-trouve-example-tip"
                variant="pill"
              />
            </div>

            {/* Cross-link to the full Meet Trouvé demo */}
            <Link
              to="/sample-routine"
              className="inline-flex items-center gap-1.5 text-sm text-[#9B6A6C] hover:text-[#805557] font-medium group"
              data-testid="meet-trouve-try-demo-link"
            >
              {t("meetTrouve.tryDemo")}
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default MeetTrouve;
